import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Icon, Select, cn } from '@forecastify/ui';
import {
  CONTROL_PANEL_PATHS,
  DEFAULT_PAGE_SIZE,
  operatorAuditListResponseSchema,
  type OperatorAuditEntry,
} from '@forecastify/shared';
import { AUDIT_ACTION_LABEL, AUDIT_RESOURCE_LABEL, labelFor } from '../audit-labels';
import { useI18n, useT } from '../i18n';
import { useOperatorSession } from '../session';
import { formatDateTime, formatNumber } from '../chrome/format';
import { Pager } from '../chrome/pager';
import { BTN_SM, SELECT_SKIN, TH, TH_ROW, TONE } from '../chrome/tones';
import { SkeletonRows } from '../chrome/skeleton-rows';
import { describeChange } from './audit-change';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ *Napló* — every operator action on the installation, filterable.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE FILTERS LIVE IN THE ADDRESS, THROUGH `useSearchParams`.** An organisation's *Teljes
 * napló* link and the deletion receipt's *Megnyitás a naplóban* both land here filtered to one
 * resource — and that resource may no longer exist, which is exactly when the log is the only record.
 * The resource filter shows as a removable chip, as in the mockup.
 *
 * ⚠️ **`automatikus` is a chip, not a blank cell.** The actor is an enum precisely so *"the clock did
 * it"* and *"somebody forgot to record who"* cannot look alike.
 *
 * ⚠️ **A deletion reads red and a suspension amber**, the redesign's two colours for the actions an
 * auditor is most often looking for.
 *
 * ⚠️ **Offset-paged with a true total** — the settled pager — where the first version had *Régebbiek*.
 */

type Load =
  | { readonly state: 'loading' }
  | { readonly state: 'failed' }
  | {
      readonly state: 'ready';
      readonly entries: readonly OperatorAuditEntry[];
      readonly total: number;
    };

const ACTION_TONE: Readonly<Record<string, string>> = {
  'tenant.delete': 'text-destructive',
  'workspace.deleted': 'text-destructive',
  'tenant.suspend': 'text-[var(--status-warn-text)]',
  'superadmin.suspend': 'text-[var(--status-warn-text)]',
  'superadmin.delete': 'text-destructive',
};

export function AuditScreen(): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  const session = useOperatorSession();
  const [search, setSearch] = useSearchParams();
  const resourceType = search.get('resourceType') ?? '';
  const action = search.get('action') ?? '';
  const resourceId = search.get('resourceId');
  const [page, setPage] = useState(0);
  const [load, setLoad] = useState<Load>({ state: 'loading' });

  /* ⚠️ `replace: true`, so eleven filter changes are not eleven Back presses. */
  const setFilter = (name: 'resourceType' | 'action' | 'resourceId', value: string): void => {
    const next = new URLSearchParams(search);
    if (value === '') next.delete(name);
    else next.set(name, value);
    setSearch(next, { replace: true });
    setPage(0);
  };

  const read = useCallback(async (): Promise<void> => {
    const query = new URLSearchParams();
    if (resourceType !== '') query.set('resourceType', resourceType);
    if (action !== '') query.set('action', action);
    if (resourceId !== null) query.set('resourceId', resourceId);
    if (page > 0) query.set('offset', String(page * DEFAULT_PAGE_SIZE));
    const suffix = query.toString();
    const result = await session.request<unknown>(
      suffix === '' ? CONTROL_PANEL_PATHS.audit : `${CONTROL_PANEL_PATHS.audit}?${suffix}`,
    );
    if (!result.ok) {
      if (result.status !== 401) setLoad({ state: 'failed' });
      return;
    }
    const parsed = operatorAuditListResponseSchema.safeParse(result.value);
    /* ⚠️ A 200 that does not match is a FAILURE: an empty audit screen claims nothing happened. */
    setLoad(
      parsed.success
        ? { state: 'ready', entries: parsed.data.data, total: parsed.data.meta.total }
        : { state: 'failed' },
    );
  }, [session, resourceType, action, resourceId, page]);

  useEffect(() => {
    void read();
  }, [read]);

  const total = load.state === 'ready' ? load.total : 0;

  return (
    <div data-screen="audit" className="flex flex-col gap-3 p-5">
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-[260px]">
          <Select
            aria-label={t('audit.filter.action')}
            icon="funnel"
            value={action}
            options={[
              { value: '', label: t('audit.filter.allActions') },
              ...Object.keys(AUDIT_ACTION_LABEL).map((key) => ({
                value: key,
                label: labelFor(AUDIT_ACTION_LABEL, key, t),
              })),
            ]}
            onChange={(event) => {
              setFilter('action', event.target.value);
            }}
            className={SELECT_SKIN}
          />
        </div>
        <div className="w-[200px]">
          <Select
            aria-label={t('audit.filter.resource')}
            value={resourceType}
            options={[
              { value: '', label: t('audit.filter.allResources') },
              ...Object.keys(AUDIT_RESOURCE_LABEL).map((key) => ({
                value: key,
                label: labelFor(AUDIT_RESOURCE_LABEL, key, t),
              })),
            ]}
            onChange={(event) => {
              setFilter('resourceType', event.target.value);
            }}
            className={SELECT_SKIN}
          />
        </div>
        {resourceId === null ? null : (
          <span className="bg-muted flex h-7 items-center gap-1 rounded-full pr-1 pl-2.5 font-mono text-[12px]">
            {resourceId.slice(0, 8)}
            <button
              type="button"
              className="hover:bg-background grid size-5 place-items-center rounded-full"
              aria-label={t('audit.filter.clearResource')}
              onClick={() => {
                setFilter('resourceId', '');
              }}
            >
              <Icon name="x" size={12} />
            </button>
          </span>
        )}
      </div>

      {load.state === 'failed' ? (
        <div role="alert" className="flex flex-col items-start gap-2">
          <p className="text-[13px]">{t('audit.failed')}</p>
          <Button
            variant="outline"
            size="sm"
            className={BTN_SM}
            onClick={() => {
              void read();
            }}
          >
            {t('audit.retry')}
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table aria-label={t('audit.heading')} className="w-full min-w-[900px] text-[13px]">
            <thead className="bg-muted/40 border-b">
              <tr className={TH_ROW}>
                <th className={TH}>{t('audit.column.when')}</th>
                <th className={TH}>{t('audit.column.actor')}</th>
                <th className={TH}>{t('audit.column.action')}</th>
                <th className={TH}>{t('audit.column.resource')}</th>
                <th className={TH}>{t('audit.column.change')}</th>
              </tr>
            </thead>
            <tbody>
              {load.state === 'loading' ? (
                <SkeletonRows columns={5} />
              ) : (
                load.entries.map((entry) => (
                  <tr key={entry.id} className="h-10 border-b last:border-0">
                    <td className="px-3 whitespace-nowrap tabular-nums">
                      {formatDateTime(entry.createdAt, locale, {
                        today: t('when.today'),
                        yesterday: t('when.yesterday'),
                      })}
                    </td>
                    <td className="px-3 whitespace-nowrap">
                      {entry.actor === 'system' ? (
                        <span
                          className={cn(
                            'inline-flex h-5 items-center gap-1 rounded-full px-2 text-[11.5px]',
                            TONE.muted,
                          )}
                        >
                          <Icon name="clock" size={11} />
                          {t('audit.actor.system')}
                        </span>
                      ) : (
                        (entry.actorName ?? entry.actorEmail ?? t('audit.actor.unknown'))
                      )}
                    </td>
                    <td
                      className={cn(
                        'px-3 font-medium whitespace-nowrap',
                        ACTION_TONE[entry.action] ?? '',
                      )}
                    >
                      {labelFor(AUDIT_ACTION_LABEL, entry.action, t)}
                    </td>
                    <td className="px-3 whitespace-nowrap">
                      <span className="text-muted-foreground text-[12px]">
                        {labelFor(AUDIT_RESOURCE_LABEL, entry.resourceType, t)}
                      </span>{' '}
                      <span className="font-mono text-[12px]">{resourceOf(entry)}</span>
                    </td>
                    <td
                      className="text-muted-foreground max-w-[340px] truncate px-3 text-[12.5px]"
                      title={describeChange(entry)}
                    >
                      {describeChange(entry) || '–'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {load.state === 'loading' ? (
            <p role="status" className="sr-only">
              {t('audit.loading')}
            </p>
          ) : null}
          {load.state === 'ready' && load.entries.length === 0 ? (
            <div className="text-muted-foreground px-6 py-10 text-center text-[12.5px]">
              {resourceType !== '' || action !== '' || resourceId !== null
                ? t('audit.emptyFiltered')
                : t('audit.empty')}
            </div>
          ) : null}
        </div>
      )}

      <Pager
        page={page}
        pageCount={Math.ceil(total / DEFAULT_PAGE_SIZE)}
        total={
          load.state === 'loading' ? '' : t('audit.count', { count: formatNumber(total, locale) })
        }
        onChange={setPage}
      />
    </div>
  );
}

/**
 * The resource's identifier as an operator would recognise it. A chore has no id — its key is in
 * the diff — and everything else gets the first eight characters of its uuid, which is enough to
 * match against a URL and short enough to scan.
 */
function resourceOf(entry: OperatorAuditEntry): string {
  if (entry.resourceId !== null) return entry.resourceId.slice(0, 8);
  const after = entry.after;
  if (after !== null && typeof after === 'object' && 'chore' in after) {
    const chore = (after as { chore?: unknown }).chore;
    if (typeof chore === 'string') return chore;
  }
  return '–';
}
